import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { Button, Stack } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { Container, Form, Label, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { Col, Row } from "reactstrap";
import { PageHeader } from "@/CommonElements/PageTitle";
import { AntSelect } from "@/CommonElements/AntSelect";
import { TableC } from "@/CommonElements/TableC";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import {
  useHousedArrivalsServiceCenterDistributionReportMutation,
  useHousedArrivalsServiceCenterDistributionReportQuery,
} from "@/services/reports";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";

const cellInt = (row, key) => {
  const parsed = Number(row?.[key]);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeOptionalNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

const housedStatusOptions = (t) => [
  {
    value: 1,
    label: t("HOUSED_STATUS_ONLY_HOUSED"),
  },
  {
    value: 0,
    label: t("HOUSED_STATUS_ALL_WITH_UNHOUSED"),
  },
];

const PilgrimMissionArrivingLeavingStatisticsReport = () => {
  const { t } = useTranslation();
  const { getLocalizedText } = useLocalizedText();
  const [reportDataRes, setReportDataRes] = useState(null);
  const [appliedFilters, setAppliedFilters] = useState(null);
  const [exportPayload, setExportPayload] = useState(null);
  const [shouldExport, setShouldExport] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      format: "json",
      orientation: "landscape",
      housed_only: undefined,
      service_center_id: undefined,
      per_page: 15,
    },
  });

  const format = watch("format");
  const isJson = format === "json";

  const {
    mutate: generateReport,
    isPending: isGeneratingReport,
  } = useHousedArrivalsServiceCenterDistributionReportMutation();

  const {
    isFetching: isExporting,
    isSuccess: isExportSuccess,
    isError: isExportError,
    error: exportError,
  } = useHousedArrivalsServiceCenterDistributionReportQuery(
    exportPayload,
    shouldExport
  );

  useEffect(() => {
    if (isExportSuccess) {
      toast.success(
        t("REPORT_EXPORTED_SUCCESSFULLY") || "Report exported successfully"
      );
      setShouldExport(false);
      setExportPayload(null);
    }
  }, [isExportSuccess, t]);

  useEffect(() => {
    if (isExportError) {
      toast.error(
        exportError?.response?.data?.message ||
          t("REPORT_EXPORT_FAILED") ||
          "Failed to export report"
      );
      setShouldExport(false);
      setExportPayload(null);
    }
  }, [exportError, isExportError, t]);

  useEffect(() => {
    if (!isJson) {
      setAppliedFilters(null);
      setReportDataRes(null);
    }
  }, [isJson]);

  const meta = reportDataRes?.data?.meta || {};
  const currentPage = appliedFilters?.page || 1;
  const pageSize = Number(meta?.per_page || appliedFilters?.per_page || 15);

  const dataWithTotals = useMemo(() => {
    const rows = reportDataRes?.data?.data || [];
    const totals = reportDataRes?.data?.meta?.totals || {};

    if (!rows.length) {
      return rows;
    }

    return [
      ...rows,
      {
        serial: "",
        service_center_id: null,
        service_center_name: t("TOTAL") ,
        campaigns_count: totals?.campaigns_count,
        groups_count: totals?.groups_count,
        actual_count: totals?.actual_count,
        from_jeddah_count: totals?.from_jeddah_count,
        from_madinah_count: totals?.from_madinah_count,
        from_taif_count: totals?.from_taif_count,
        from_yanbu_count: totals?.from_yanbu_count,
        is_total_row: true,
      },
    ];
  }, [reportDataRes, t]);

  const columns = useMemo(
    () => [
      {
        name: t("SERIAl_NUMBER"),
        key: "serial",
        filterable: false,
        render: (_, row, index) => {
          if (row?.is_total_row) {
            return "";
          }

          if (
            row?.serial !== undefined &&
            row?.serial !== null &&
            row?.serial !== ""
          ) {
            return row.serial;
          }

          return (currentPage - 1) * pageSize + index + 1;
        },
      },
      {
        name: t("SERVICE_CENTER"),
        key: "service_center_name",
        filterable: false,
        render: (_, row) => row?.service_center_name || "-",
      },
      {
        name: t("CAMPAIGNS") || "Campaigns",
        key: "campaigns_count",
        filterable: false,
        render: (_, row) => cellInt(row, "campaigns_count"),
      },
      {
        name: t("GROUPS") || "Groups",
        key: "groups_count",
        filterable: false,
        render: (_, row) => cellInt(row, "groups_count"),
      },
      {
        name: t("PILGRIMS_COUNT"),
        key: "actual_count",
        filterable: false,
        render: (_, row) => cellInt(row, "actual_count"),
      },
      {
        name: t("JEDDAH"),
        key: "from_jeddah_count",
        filterable: false,
        render: (_, row) => cellInt(row, "from_jeddah_count"),
      },
      {
        name: t("MADINAH"),
        key: "from_madinah_count",
        filterable: false,
        render: (_, row) => cellInt(row, "from_madinah_count"),
      },
      {
        name: t("TAIF"),
        key: "from_taif_count",
        filterable: false,
        render: (_, row) => cellInt(row, "from_taif_count"),
      },
      {
        name: t("YANBU"),
        key: "from_yanbu_count",
        filterable: false,
        render: (_, row) => cellInt(row, "from_yanbu_count"),
      },
    ],
    [currentPage, pageSize, t]
  );

  const fetchJsonReport = (payload) => {
    generateReport(payload, {
      onSuccess: (response) => {
        setReportDataRes(response);
      },
    });
  };

  const onSubmit = (data) => {
    const housedOnly = normalizeOptionalNumber(data.housed_only);

    if (data.format === "json") {
      const nextFilters = {
        format: "json",
        orientation: data.orientation,
        housed_only: housedOnly,
        service_center_id: data.service_center_id,
        per_page: Number(data.per_page) || 15,
        page: 1,
      };

      setAppliedFilters(nextFilters);
      fetchJsonReport(nextFilters);
      return;
    }

    setExportPayload({
      ...data,
      housed_only: housedOnly,
    });
    setShouldExport(true);
  };

  const handleReset = () => {
    reset({
      format: "json",
      orientation: "landscape",
      housed_only: 1,
      service_center_id: undefined,
      per_page: 15,
    });
    setAppliedFilters(null);
    setReportDataRes(null);
    setExportPayload(null);
    setShouldExport(false);
  };

  const isLoading = isGeneratingReport || isExporting;

  return (
    <Stack gap={3}>
      <PageHeader
        title={t("PILGRIM_MISSION_ARRIVING_LEAVING_STATISTICS_REPORT")}
        sticky={false}
      />

      <Container fluid>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            {/* format */}
            <Col md={6}>
              <Stack gap={1} className="mb-3">
                <Label className="fw-bold" for="report-format">
                  {t("REPORT_FORMAT")}
                </Label>
                <Controller
                  name="format"
                  control={control}
                  render={({ field }) => (
                    <AntSelect
                      {...field}
                      id="report-format"
                      options={[
                        { value: "json", label: t("VIEW_REPORTS") },
                        { value: "pdf", label: t("EXPORT_PDF") },
                        { value: "excel", label: t("EXPORT_EXCEL") },
                      ]}
                    />
                  )}
                />
              </Stack>
            </Col>

            {/* orientation / per page */}
            <Col md={6}>
              <Stack gap={1} className="mb-3">
                <Label
                  className="fw-bold"
                  for={isJson ? "per-page" : "report-orientation"}
                >
                  {isJson ? t("PER_PAGE") : t("ORIENTATION")}
                </Label>
                {isJson ? (
                  <Controller
                    name="per_page"
                    control={control}
                    render={({ field }) => (
                      <AntSelect
                        {...field}
                        id="per-page"
                        options={[
                          { value: 10, label: "10" },
                          { value: 15, label: "15" },
                          { value: 20, label: "20" },
                          { value: 50, label: "50" },
                        ]}
                      />
                    )}
                  />
                ) : (
                  <Controller
                    name="orientation"
                    control={control}
                    render={({ field }) => (
                      <AntSelect
                        {...field}
                        id="report-orientation"
                        options={[
                          { value: "portrait", label: t("PORTRAIT") },
                          { value: "landscape", label: t("LANDSCAPE") },
                        ]}
                      />
                    )}
                  />
                )}
              </Stack>
            </Col>
          </Row>

          <Row>
            {/* service center */}
            <Col md={6}>
              <Stack gap={1} className="mb-3">
                <Label className="fw-bold" for="service-center-id">
                  {t("SERVICE_CENTER")}
                </Label>
                <Controller
                  name="service_center_id"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <SelectCachedOptions
                      {...field}
                      allowClear
                      showSearch
                      id="service-center-id"
                      optionKey="service_centers"
                      status={error ? "error" : undefined}
                      placeholder={
                        t("SELECT_SERVICE_CENTER") || "Select service center"
                      }
                      options={(serviceCenter) => ({
                        value: serviceCenter.id,
                        label:
                          getLocalizedText(
                            serviceCenter?.name_ar,
                            serviceCenter?.name_en
                          ) ||
                          serviceCenter?.name ||
                          serviceCenter?.id,
                      })}
                    />
                  )}
                />
              </Stack>
            </Col>

            {/* housed only */}
            {/* <Col md={6}>
              <Stack gap={1} className="mb-3">
                <Label className="fw-bold" for="housed-only">
                  {t("STATUS") || "Status"}
                </Label>
                <Controller
                  name="housed_only"
                  control={control}
                  render={({ field }) => (
                    <AntSelect
                      allowClear
                      id="housed-only"
                      placeholder={t("SELECT_STATUS") || "Select status"}
                      options={housedStatusOptions(t)}
                      name={field.name}
                      ref={field.ref}
                      value={field.value ?? undefined}
                      onBlur={field.onBlur}
                      onChange={(value) => field.onChange(value ?? null)}
                      onClear={() => field.onChange(null)}
                    />
                  )}
                />
              </Stack>
            </Col> */}
          </Row>

          <ActionsFooter>
            <Button variant="primary" type="submit" disabled={isLoading}>
              {isLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  {t("LOADING") || "Loading..."}
                </>
              ) : isJson ? (
                t("VIEW_REPORTS")
              ) : format === "pdf" ? (
                t("EXPORT_PDF")
              ) : (
                t("EXPORT_EXCEL")
              )}
            </Button>

            <Button
              variant="secondary"
              type="button"
              onClick={handleReset}
              disabled={isLoading}
            >
              {t("RESET") || "Reset"}
            </Button>
          </ActionsFooter>
        </Form>
      </Container>

      {isJson && appliedFilters !== null && (
        <Container fluid>
          <TableC
            columns={columns}
            data={dataWithTotals}
            loading={isGeneratingReport}
            columnsFilter={false}
            emptyStateTitle={t("NO_DATA") || "No data"}
            emptyStateDescription={
              t("NO_DATA_DESCRIPTION") || "No data found"
            }
            getRowStyle={(row) =>
              row?.is_total_row
                ? {
                    fontWeight: 700,
                    backgroundColor: "rgba(0, 0, 0, 0.03)",
                  }
                : {}
            }
            pagination={{
              current: currentPage,
              total: meta?.total || 0,
              pageSize,
              onChange: (page) => {
                const nextFilters = {
                  ...appliedFilters,
                  page,
                };

                setAppliedFilters(nextFilters);
                fetchJsonReport(nextFilters);
              },
            }}
          />
        </Container>
      )}
    </Stack>
  );
};

export default PilgrimMissionArrivingLeavingStatisticsReport;
